<div class="custom-dropdown relative w-full grid gap-y-1 flex-col">
  <!-- Trigger / Selected chips area -->
  <div
    class="dropdown-toggle w-full min-w-0 flex px-3 items-center gap-2 min-h-12 rounded border border-solid"
    [ngClass]="{
      'cursor-not-allowed': isDisabled,
      'cursor-pointer': !isDisabled,
      'border-color-light-shade': !isInvalid && !isDisabled,
      'border-[#00000012]': isDisabled,
      'border-color-danger': isInvalid,
    }"
    (click)="toggleDropdown()"
  >
    <!-- Floating label -->
    <span
      class="absolute label leading-3 text-sm bg-color-primary-contrast"
      [ngClass]="{
        ' top-1/2 -translate-y-1/2': selectedItems().length <= 0,
        'label-float text-xs': selectedItems().length > 0,
        'text-color-secondary': !isDisabled && selectedItems().length > 0,
        'text-[#00000061]': isDisabled,
        'text-color-medium': !isInvalid && !isDisabled && !selectedItems().length,
        'text-color-danger': isInvalid,
      }"
    >
      {{ placeholder }}
      @if (isRequired) {
        *
      }
    </span>

    <!-- Chips of selected items -->
    @if (selectedItems().length > 0) {
      <div class="flex-1 flex gap-1 items-center overflow-hidden w-0">
        @for (item of selectedItems() | slice: 0 : maxVisibleChips; track $index) {
          <span
            class="chip flex items-center gap-1 text-xs rounded-full px-2 py-1 my-1 min-w-0 max-w-full"
            (click)="$event.stopPropagation()"
          >
            <span class="truncate max-w-[120px] flex-1 min-w-0" [title]="item?.name">{{ item?.name }}</span>
            <mat-icon
              class="chip-remove text-sm leading-none cursor-pointer shrink-0"
              [ngClass]="{ 'pointer-events-none': isDisabled }"
              (click)="removeChip(item)"
              >close</mat-icon
            >
          </span>
        }
        @if (selectedItems().length > maxVisibleChips) {
          <span class="chip flex items-center text-xs rounded-full px-2 py-1 my-1 shrink-0">
            +{{ selectedItems().length - maxVisibleChips }}
          </span>
        }
      </div>
    }

    <!-- Right arrow + clear all -->
    <span class="ml-auto flex items-center gap-1 shrink-0">
      @if (selectedItems().length > 0 && !isDisabled) {
        <mat-icon
          class="text-color-secondary cursor-pointer hover:text-opacity-80 text-xl"
          (click)="$event.stopPropagation(); clearAll()"
          >close</mat-icon
        >
      }
      <mat-icon
        [ngClass]="{
          'text-color-danger': isInvalid,
          'text-color-secondary': !isInvalid && !isDisabled,
          'text-[#00000061]': isDisabled,
        }"
        >arrow_drop_down</mat-icon
      >
    </span>
  </div>

  <!-- Dropdown panel -->
  @if (isOpen()) {
    <div
      class="dropdown-content absolute w-full rounded bg-color-primary-contrast border-solid border"
      [ngClass]="{
        above: isOptionsAbove,
        below: !isOptionsAbove,
        'border-color-light-shade': !isDisabled,
        'border-[#00000012]': isDisabled,
      }"
    >
      <!-- Search bar -->
      <div
        class="flex items-center gap-2 px-4 border-solid border-0"
        [ngClass]="{
          'border-t': isOptionsAbove,
          'border-b': !isOptionsAbove,
          'border-color-border-medium-contrast': !isDisabled,
          'border-[#00000012]': isDisabled,
        }"
      >
        <mat-icon class="text-color-primary min-w-[24px]">search</mat-icon>
        <input
          #inputElement
          class="text-sm focus:outline-none h-12 placeholder-color-medium grow border-transparent"
          type="text"
          [formControl]="searchTerm"
          [placeholder]="searchPlaceholder || 'FC_SELECT.PLACEHOLDER' | translate"
          (input)="scrollbarRef.scrollTo({ top: 0 })"
        />
        @if (searchTerm.value) {
          <mat-icon class="text-color-primary cursor-pointer hover:text-opacity-80 min-w-[24px]" (click)="searchTerm.setValue('')"
            >highlight_off</mat-icon
          >
        }
      </div>
      <!-- Progress bar -->
      @if (isLoading()) {
        <mat-progress-bar class="w-full" mode="indeterminate"></mat-progress-bar>
      }
      <!-- Options list -->
      <ul #optionsList class="options-list list-none m-0 p-0">
        <ng-scrollbar
          #scrollbarRef
          (updated)="onScrollbarUpdate(scrollbarRef)"
          [visibility]="'hover'"
          autoHeightDisabled="false"
          class="max-h-[262px]"
        >
          @if (!options().length && !isLoading()) {
            <li class="flex items-center gap-x-2 text-color-normal text-sm py-3 px-4">
              {{ 'FC_SELECT.NO_OPTIONS_FOUND' | translate }}
            </li>
          }
          @for (item of options(); track $index) {
            <li
              [hidden]="ignoredIds.includes(item.id)"
              class="option-item flex items-center gap-x-3 text-color-normal hover:bg-color-light text-sm py-3 px-4 cursor-pointer"
              [class.selected]="isSelected(item)"
              (click)="toggleItem(item)"
            >
              <!-- Checkbox visual -->
              <span
                class="checkbox-box flex items-center justify-center w-4 h-4 rounded border border-solid shrink-0"
                [class.checked]="isSelected(item)"
              >
                @if (isSelected(item)) {
                  <mat-icon class="text-sm leading-none check-icon">check</mat-icon>
                }
              </span>
              @if (showImage) {
                <img
                  appImageLoad
                  (error)="item.avatarUrl = null"
                  [src]="item.avatarUrl || 'https://ui-avatars.com/api/?name=' + (item?.name || '')"
                  alt="item.name"
                  class="w-6 h-6 rounded-full"
                />
              }
              <span tooltipWhenTruncated="{{ item.name }}" matTooltip class="truncate">{{ item.name }}</span>
            </li>
          }
        </ng-scrollbar>
      </ul>
      <!-- Selection summary footer -->
      @if (selectedItems().length > 0) {
        <div
          class="selection-footer flex items-center justify-between px-4 py-2 border-solid border-0"
          [ngClass]="{
            'border-b': isOptionsAbove,
            'border-t': !isOptionsAbove,
            'border-color-border-medium-contrast': true,
          }"
        >
          <span class="text-xs text-color-secondary">
            {{ selectedItems().length }} {{ 'FC_SELECT_MULTIPLE.SELECTED' | translate }}
          </span>
          <button
            type="button"
            class="text-xs text-color-primary cursor-pointer bg-transparent border-0 p-0 underline"
            (click)="clearAll()"
          >
            {{ 'FC_SELECT_MULTIPLE.CLEAR_ALL' | translate }}
          </button>
        </div>
      }
    </div>
  }
</div>
